import React, { useEffect, useState } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Chip,
  Link,
  CircularProgress,
  Box,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import RefreshIcon from '@material-ui/icons/Refresh';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import { useEntity } from '@backstage/plugin-catalog-react';
import { useApi, discoveryApiRef, identityApiRef } from '@backstage/core-plugin-api';

const useStyles = makeStyles(theme => ({
  card: {
    marginBottom: theme.spacing(2),
  },
  statusChip: {
    fontWeight: 'bold',
  },
  completed: {
    backgroundColor: '#4caf50',
    color: 'white',
  },
  failed: {
    backgroundColor: '#f44336',
    color: 'white',
  },
  processing: {
    backgroundColor: '#2196f3',
    color: 'white',
  },
  pending: {
    backgroundColor: '#ff9800',
    color: 'white',
  },
  cancelled: {
    backgroundColor: '#9e9e9e',
    color: 'white',
  },
  emptyMessage: {
    textAlign: 'center',
    padding: theme.spacing(4),
    color: theme.palette.text.secondary,
  },
  tableCell: {
    maxWidth: 200,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
}));

interface Task {
  id: string;
  spec: {
    templateInfo?: {
      entityRef: string;
    };
    user?: {
      ref: string;
    };
  };
  status: string;
  createdAt: string;
  lastHeartbeatAt?: string;
}

export const TemplateTasksCard = () => {
  const classes = useStyles();
  const { entity } = useEntity();
  const discoveryApi = useApi(discoveryApiRef);
  const identityApi = useApi(identityApiRef);
  
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const templateRef = `template:${entity.metadata.namespace || 'default'}/${entity.metadata.name}`;

  const fetchTasks = async () => {
    setLoading(true);
    setError(null);
    try {
      const baseUrl = await discoveryApi.getBaseUrl('scaffolder');
      const { token } = await identityApi.getCredentials();
      
      // API 不支持按 template 过滤，需要获取全部任务后在前端过滤
      // 确保 baseUrl 末尾没有斜杠，避免 URL 拼接问题
      const normalizedBaseUrl = baseUrl.replace(/\/$/, '');
      const response = await fetch(
        `${normalizedBaseUrl}/v2/tasks`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      
      if (!response.ok) {
        throw new Error(`Failed to fetch tasks: ${response.statusText}`);
      }
      
      const data = await response.json();
      
      // 在前端过滤出属于当前 Template 的任务
      const filteredTasks = (data.tasks || []).filter((task: Task) => 
        task.spec.templateInfo?.entityRef === templateRef
      );
      
      // 按创建时间倒序排列
      filteredTasks.sort((a: Task, b: Task) => 
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );
      
      setTasks(filteredTasks);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTasks();
  }, [templateRef]);

  const getStatusChipClass = (status: string) => {
    switch (status) {
      case 'completed':
        return classes.completed;
      case 'failed':
        return classes.failed;
      case 'processing':
        return classes.processing;
      case 'pending':
        return classes.pending;
      case 'cancelled':
        return classes.cancelled;
      default:
        return '';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'completed':
        return '✓ 完成';
      case 'failed':
        return '✗ 失败';
      case 'processing':
        return '⟳ 执行中';
      case 'pending':
        return '○ 等待';
      case 'cancelled':
        return '◌ 已取消';
      default:
        return status;
    }
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleString('zh-CN', {
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getUserName = (userRef?: string) => {
    if (!userRef) return '-';
    // userRef 格式: user:default/username
    const parts = userRef.split('/');
    return parts[parts.length - 1];
  };

  return (
    <Card className={classes.card}>
      <CardHeader
        title="任务执行历史"
        subheader={`共 ${tasks.length} 次执行`}
        action={
          <IconButton onClick={fetchTasks} disabled={loading}>
            <RefreshIcon />
          </IconButton>
        }
      />
      <CardContent>
        {loading && (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        )}
        
        {error && (
          <Typography color="error" className={classes.emptyMessage}>
            加载失败: {error}
          </Typography>
        )}
        
        {!loading && !error && tasks.length === 0 && (
          <Typography className={classes.emptyMessage}>
            暂无执行记录
          </Typography>
        )}
        
        {!loading && !error && tasks.length > 0 && (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>状态</TableCell>
                <TableCell>执行者</TableCell>
                <TableCell>创建时间</TableCell>
                <TableCell>操作</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tasks.slice(0, 10).map(task => (
                <TableRow key={task.id}>
                  <TableCell>
                    <Chip
                      label={getStatusLabel(task.status)}
                      size="small"
                      className={`${classes.statusChip} ${getStatusChipClass(task.status)}`}
                    />
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    {getUserName(task.spec.user?.ref)}
                  </TableCell>
                  <TableCell>{formatDate(task.createdAt)}</TableCell>
                  <TableCell>
                    <Link href={`/create/tasks/${task.id}`}>
                      <IconButton size="small" title="查看详情">
                        <OpenInNewIcon fontSize="small" />
                      </IconButton>
                    </Link>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        
        {!loading && !error && tasks.length > 10 && (
          <Box mt={2} textAlign="center">
            <Link href="/create/tasks">
              查看全部 {tasks.length} 条记录
            </Link>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

